"use client";

import React, { useMemo, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { Loader2 } from "lucide-react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";

// Dynamically import gamification components (client-only)
const ClassicQuizPlay = dynamic(
    () => import("fansunited-frontend-components/ClassicQuizPlay").then(m => ({ default: m.ClassicQuizPlay })),
    { ssr: false }
);

const PollVote = dynamic(
    () => import("fansunited-frontend-components/PollVote").then(m => ({ default: m.PollVote })),
    { ssr: false }
);

const PersonalityQuizPlay = dynamic(
    () => import("fansunited-frontend-components/PersonalityQuizPlay").then(m => ({ default: m.PersonalityQuizPlay })),
    { ssr: false }
);

const MatchQuizPlay = dynamic(
    () => import("fansunited-frontend-components/MatchQuizPlay").then(m => ({ default: m.MatchQuizPlay })),
    { ssr: false }
);

const EventGamePlay = dynamic(
    () => import("fansunited-frontend-components/EventGamePlay").then(m => ({ default: m.EventGamePlay })),
    { ssr: false }
);

import type FansUnitedSDKModel from "fansunited-sdk-esm/Core/Global/Models/FansUnitedSDKModel";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { mapTenantToFUWidgetTheme, type FUWidgetThemeOptions } from "@/features/theme/fu-widget-theme";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { getWidgetLanguage, type WidgetLanguage } from "@/lib/labels/widgetLanguage";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useAuth } from "@/lib/auth/AuthContext";
import { markActivity } from "@/lib/auth/activityTracker";

export type GamificationType = "classic-quiz" | "personality-quiz" | "match-quiz" | "either-or" | "poll" | "event-game";

/**
 * Widget template enum - matches WidgetTemplate from fansunited-frontend-core
 */
export enum WidgetTemplate {
    STANDARD = "standard",
    OVERLAY = "overlay",
    SPLIT = "split",
}

export interface GamificationWrapperProps {
    /** Type of gamification component to render */
    type: GamificationType;
    /** ID of the game/poll (entityId) */
    id: string;
    /** FansUnited SDK instance (optional - will use useFansUnitedSDK if not provided) */
    sdk?: FansUnitedSDKModel;
    /** Widget template style */
    template?: WidgetTemplate;
    /** Widget language */
    language?: WidgetLanguage;
    /** Theme options for the gamification component (mapped from tenant theme) */
    themeOptions?: FUWidgetThemeOptions;
    /** Additional props to pass to the component */
    componentProps?: Record<string, any>;
}

/**
 * GamificationWrapper - Wrapper component for FansUnited gamification components.
 *
 * Handles:
 * - SDK integration (uses useFansUnitedSDK hook if not provided)
 * - Component selection based on type
 * - Consistent prop passing (entityId, sdk, template, language)
 *
 * @example
 * ```tsx
 * <GamificationWrapper
 *   type="classic-quiz"
 *   id="quiz-123"
 *   template={WidgetTemplate.STANDARD}
 *   language="en"
 * />
 * ```
 */
export const GamificationWrapper: React.FC<GamificationWrapperProps> = ({
    type,
    id,
    sdk: providedSdk,
    template = WidgetTemplate.OVERLAY,
    language: providedLanguage,
    themeOptions: providedThemeOptions,
    componentProps = {},
}) => {
    // Defer rendering until after hydration to avoid server/client mismatch
    // (SDK is null on server but available on client via context provider)
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // Derive widget language from locale context (or use explicit override)
    const locale = useLocale();
    const language = providedLanguage ?? getWidgetLanguage(locale);

    // Use provided SDK or get from context
    const contextSdk = useFansUnitedSDK();
    const sdk = providedSdk || contextSdk;

    // Get tenant settings from context to automatically apply theme
    const tenantSettings = useTenantSettings();
    const labels = useLabels();
    const nav = useNavigation();
    const { user } = useAuth();

    // Memoize theme options to avoid recalculating on every render
    const themeOptions = useMemo(() => {
        // Use provided theme options if available, otherwise map from tenant settings
        if (providedThemeOptions) {
            return providedThemeOptions;
        }
        return mapTenantToFUWidgetTheme(tenantSettings);
    }, [providedThemeOptions, tenantSettings]);

    // Build signInCTA — use poll-specific label for polls, game label for everything else
    const signInCTA = useMemo(() => {
        const defaultLabel = type === "poll"
            ? labels.teamPage.gamification.signInToVote
            : labels.teamPage.gamification.signInToPlay;
        const signInUrl = nav.signIn();
        return {
            defaultLabel,
            url: signInUrl,
            target: "_self" as const,
        };
    }, [type, labels, nav]);

    // Show loading spinner until mounted and SDK is available.
    // min-h-64 reserves space to reduce CLS when the widget loads.
    if (!mounted || !sdk) {
        return (
            <div className="flex items-center justify-center p-8 min-h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Common props for all components
    const commonProps = {
        entityId: id,
        sdk,
        template,
        language,
        themeOptions,
        signInCTA,
        userIsLoggedIn: !!user,
        ...(tenantSettings.gamificationDefaultBgUrl && {
            defaultImagePlaceholderUrl: tenantSettings.gamificationDefaultBgUrl,
        }),
        ...componentProps,
        // Every game type reports completion via callbacks.onFinish — flag it so anon users get the upgrade nudge
        callbacks: {
            ...componentProps.callbacks,
            onFinish: (...args: unknown[]) => {
                if (user?.uid) markActivity(user.uid);
                componentProps.callbacks?.onFinish?.(...args);
            },
        },
    };

    // Render the appropriate component based on type
    switch (type) {
        case "classic-quiz":
            return <ClassicQuizPlay {...commonProps} />;

        case "personality-quiz":
            return <PersonalityQuizPlay {...commonProps} />;

        case "match-quiz":
            return <MatchQuizPlay {...commonProps} />;

        case "either-or":
            // Either/Or games use the EventGamePlay component
            return <EventGamePlay {...commonProps} />;

        case "poll":
            return <PollVote {...commonProps} />;

        case "event-game":
            return <EventGamePlay {...commonProps} />;

        default:
            return (
                <div className="p-8 text-center">
                    <p className="text-muted-foreground">Unsupported game type: {type}</p>
                </div>
            );
    }
};
